import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Quotes, ArrowLeft, ArrowRight, EnvelopeSimple, X, CheckCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export default function TestimonialsBand() {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [formOpen, setFormOpen] = useState(false);
  const trackRef = useRef(null);

  useEffect(() => {
    api.get("/testimonials?limit=10").then(({ data }) => setItems(data || [])).catch(() => setItems([]));
    // Auto-open the submission modal when the URL carries ?temoignage=open (used by post-retrouvaille emails)
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("temoignage") === "open") {
        setFormOpen(true);
        // Scroll the testimonial band into view
        setTimeout(() => {
          document.querySelector('[data-testid="testimonials-band"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
          document.querySelector('[data-testid="testimonials-empty"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 400);
      }
    } catch { /* SSR fallback */ }
  }, []);

  if (items.length === 0) {
    return (
      <>
        <ContributeBlock variant="section" onOpen={() => setFormOpen(true)} />
        {formOpen && <SubmitModal onClose={() => setFormOpen(false)} />}
      </>
    );
  }

  const go = (delta) => {
    setIdx((i) => {
      const next = (i + delta + items.length) % items.length;
      const card = trackRef.current?.children?.[next];
      card?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
      return next;
    });
  };

  return (
    <section className="relative py-16 sm:py-20 px-4 bg-gradient-to-b from-rose-50/70 to-orange-50/70 overflow-hidden" data-testid="testimonials-band">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col items-center text-center mb-10">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/70 text-rose-700 font-bold text-xs uppercase tracking-[0.22em] mb-3">
            <Quotes size={14} weight="fill" /> Ils nous ont fait confiance
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black text-stone-800 max-w-2xl">
            De vraies histoires, <span className="text-orange-500">de vraies retrouvailles</span>.
          </h2>
        </div>

        <div className="relative">
          <div
            ref={trackRef}
            className="flex gap-5 overflow-x-auto snap-x snap-mandatory pb-4 -mx-4 px-4 scrollbar-none"
            style={{ scrollBehavior: "smooth" }}
            data-testid="testimonials-track"
          >
            {items.map((t, i) => (
              <article
                key={t.testimonial_id}
                onMouseEnter={() => setIdx(i)}
                className="snap-center shrink-0 w-[88%] sm:w-[440px] bg-white rounded-3xl border-2 border-rose-100 shadow-[0_18px_40px_-20px_rgba(244,63,94,0.25)] p-6 sm:p-8 relative"
                data-testid={`testimonial-card-${t.testimonial_id}`}
              >
                <Quotes size={36} weight="fill" className="absolute -top-4 left-6 text-orange-400 bg-white rounded-full p-1.5 shadow" />
                <p className="text-stone-700 leading-relaxed whitespace-pre-wrap text-[15px] sm:text-base">"{t.text}"</p>
                <div className="mt-5 flex items-center gap-3">
                  {t.photo ? (
                    <img src={t.photo} alt={t.first_name} className="w-12 h-12 rounded-2xl object-cover" />
                  ) : (
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-200 to-rose-200 grid place-items-center font-display font-black text-orange-700">
                      {t.first_name?.[0]?.toUpperCase() || "•"}
                    </div>
                  )}
                  <div>
                    <p className="font-display font-black text-stone-800">{t.first_name}{t.city ? <span className="font-normal text-stone-500"> · {t.city}</span> : null}</p>
                    {t.date_label && <p className="text-xs text-stone-400">{t.date_label}</p>}
                  </div>
                </div>
              </article>
            ))}
          </div>

          {items.length > 1 && (
            <>
              <button onClick={() => go(-1)} aria-label="Précédent" className="hidden sm:grid place-items-center absolute left-2 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white shadow-lg border border-rose-100 text-orange-600 hover:bg-orange-50" data-testid="testimonials-prev">
                <ArrowLeft size={18} weight="bold" />
              </button>
              <button onClick={() => go(1)} aria-label="Suivant" className="hidden sm:grid place-items-center absolute right-2 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white shadow-lg border border-rose-100 text-orange-600 hover:bg-orange-50" data-testid="testimonials-next">
                <ArrowRight size={18} weight="bold" />
              </button>
            </>
          )}
        </div>

        <ContributeBlock variant="inline" onOpen={() => setFormOpen(true)} />
      </div>
      {formOpen && <SubmitModal onClose={() => setFormOpen(false)} />}
    </section>
  );
}

function ContributeBlock({ variant = "section", onOpen }) {
  if (variant === "inline") {
    return (
      <div className="mt-10 rounded-3xl border-2 border-dashed border-orange-300 bg-white/60 backdrop-blur p-6 sm:p-8 text-center" data-testid="testimonials-contribute-inline">
        <p className="font-display text-xl sm:text-2xl font-black text-stone-800">Vous aussi, vous avez une belle histoire à raconter&nbsp;?</p>
        <p className="text-stone-600 mt-1 text-sm sm:text-base">Quelques lignes suffisent. Votre histoire pourra être publiée ici pour aider d'autres familles à garder espoir.</p>
        <button onClick={onOpen} data-testid="testimonials-open-form" className="inline-flex items-center gap-2 mt-5 px-6 py-3 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-bold shadow-lg shadow-orange-500/30 transition">
          <EnvelopeSimple size={18} weight="bold" /> Envoyer mon témoignage
        </button>
      </div>
    );
  }

  return (
    <section className="py-16 sm:py-20 px-4 bg-gradient-to-b from-rose-50/70 to-orange-50/70" data-testid="testimonials-empty">
      <div className="max-w-3xl mx-auto text-center rounded-3xl border-2 border-dashed border-orange-300 bg-white/70 backdrop-blur p-8 sm:p-10">
        <Quotes size={40} weight="duotone" className="mx-auto text-orange-500" />
        <h2 className="font-display text-3xl sm:text-4xl font-black text-stone-800 mt-3">Bientôt, vos histoires&nbsp;ici.</h2>
        <p className="text-stone-600 mt-2">Vous avez retrouvé un doudou grâce à Doudou Alert&nbsp;? Racontez-nous&nbsp;: votre histoire pourra être publiée pour redonner espoir à d'autres familles.</p>
        <button onClick={onOpen} data-testid="testimonials-open-form" className="inline-flex items-center gap-2 mt-6 px-6 py-3 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-bold shadow-lg shadow-orange-500/30">
          <EnvelopeSimple size={18} weight="bold" /> Envoyer mon témoignage
        </button>
      </div>
    </section>
  );
}

function SubmitModal({ onClose }) {
  const [form, setForm] = useState({ first_name: "", email: "", city: "", text: "", date_label: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.text.trim().length < 10) { toast.error("Témoignage trop court (10 caractères minimum)"); return; }
    setSubmitting(true);
    try {
      await api.post("/testimonials/submit", form);
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi");
    }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-stone-900/60 backdrop-blur-sm grid place-items-center p-4" onClick={onClose} data-testid="testimonial-submit-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl max-w-xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-rose-100 px-6 py-4 flex items-center justify-between">
          <p className="font-display text-xl font-black text-stone-800">Envoyer mon témoignage</p>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-700" data-testid="testimonial-submit-close"><X size={22} /></button>
        </div>

        {done ? (
          <div className="p-8 text-center">
            <CheckCircle size={56} weight="duotone" className="mx-auto text-emerald-500" />
            <p className="font-display text-2xl font-black text-stone-800 mt-3">Merci infiniment&nbsp;!</p>
            <p className="text-stone-600 mt-2">Votre témoignage a bien été envoyé. Vous allez recevoir un email de confirmation avec une copie de votre message dans quelques instants. 💌</p>
            <Button onClick={onClose} className="mt-6 bg-orange-500 hover:bg-orange-600 rounded-full font-bold px-6" data-testid="testimonial-submit-done-close">Fermer</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4" data-testid="testimonial-submit-form">
            <p className="text-sm text-stone-600">Vos retrouvailles ont touché votre famille&nbsp;? Partagez votre histoire pour redonner espoir à d'autres. Vous recevrez une copie par email avant publication.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Prénom *</Label>
                <Input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder="Sophie" className="rounded-2xl mt-1" data-testid="testimonial-input-firstname" />
              </div>
              <div>
                <Label>Email *</Label>
                <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="sophie@exemple.fr" className="rounded-2xl mt-1" data-testid="testimonial-input-email" />
                <p className="text-xs text-stone-400 mt-1">Confidentiel, jamais publié.</p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Ville (facultatif)</Label>
                <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Toulouse" className="rounded-2xl mt-1" data-testid="testimonial-input-city" />
              </div>
              <div>
                <Label>Date / période (facultatif)</Label>
                <Input value={form.date_label} onChange={(e) => setForm({ ...form, date_label: e.target.value })} placeholder="Mai 2025" className="rounded-2xl mt-1" data-testid="testimonial-input-date" />
              </div>
            </div>
            <div>
              <Label>Votre témoignage *</Label>
              <Textarea required rows={6} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} placeholder="Racontez-nous ce qui s'est passé, ce que vous avez ressenti…" className="rounded-2xl mt-1" data-testid="testimonial-input-text" />
              <p className="text-xs text-stone-400 mt-1">{form.text.length} caractère{form.text.length > 1 ? "s" : ""} (10 min — 1200 max)</p>
            </div>
            <p className="text-xs text-stone-500 bg-rose-50 rounded-2xl p-3">
              En envoyant, vous nous autorisez à publier ce texte avec votre <strong>prénom</strong> uniquement sur doudou-alert.fr. Votre email reste confidentiel.
            </p>
            <Button type="submit" disabled={submitting} className="w-full bg-orange-500 hover:bg-orange-600 rounded-full py-6 font-bold" data-testid="testimonial-submit-button">
              {submitting ? "Envoi en cours..." : "Envoyer mon témoignage"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
